<template>
  <div class="org-detail pa-4">
    <v-btn text small class="mb-2 px-1" :to="{ name: 'ClientOrgCharts' }">
      <v-icon small left>mdi-arrow-left</v-icon>
      Organigramas
    </v-btn>

    <div v-if="loadingOrg && !organization" class="d-flex flex-column align-center py-12">
      <v-progress-circular indeterminate color="primary" />
      <span class="pw-label mt-3">Cargando desde Pipedrive…</span>
    </div>

    <v-alert v-else-if="loadError" type="warning" text>{{ loadError }}</v-alert>

    <template v-else-if="organization">
      <div class="d-flex flex-wrap align-center mb-4">
        <div class="mr-4">
          <h2 class="org-detail__title">{{ organization.name }}</h2>
          <div class="org-detail__meta">
            <span v-if="organization.country">{{ organization.country }}</span>
            <span v-if="organization.country && organization.companyType"> · </span>
            <span v-if="organization.companyType">{{ organization.companyType }}</span>
          </div>
        </div>
        <v-spacer />
        <div class="d-flex flex-wrap align-center org-detail__actions">
          <v-btn
            v-if="organization.pipedriveUrl"
            text
            :href="organization.pipedriveUrl"
            target="_blank"
            rel="noopener"
          >
            <v-icon small left>mdi-open-in-new</v-icon>
            Ver en Pipedrive
          </v-btn>
          <v-btn v-if="can('client-org-charts:update')" color="primary" depressed @click="addOpen = true">
            <v-icon left>mdi-account-plus-outline</v-icon>
            Agregar persona
          </v-btn>
        </div>
      </div>

      <v-tabs v-model="tab" class="mb-4">
        <v-tab>Organigrama</v-tab>
        <v-tab>Por revisar ({{ pendingCandidates }})</v-tab>
        <v-tab>Notas</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item>
          <v-row>
            <v-col cols="12" md="8" lg="9" class="org-detail__tree-col">
              <v-card>
                <v-card-text class="pb-0">
                  <v-autocomplete
                    v-model="searchPersonId"
                    :items="searchItems"
                    item-text="name"
                    item-value="personId"
                    label="Buscar persona"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    outlined
                    dense
                    hide-details
                    class="org-detail__search"
                  />
                </v-card-text>
                <org-chart-tree
                  :nodes="nodes"
                  :areas="areas"
                  :highlight-person-id="highlightPersonId"
                  class="mt-2"
                  @select-person="openPanel"
                />
              </v-card>
            </v-col>

            <v-col cols="12" md="4" lg="3">
              <v-card>
                <v-card-title class="org-detail__section">Sin ubicar ({{ unplaced.length }})</v-card-title>
                <v-card-text>
                  <p v-if="!unplaced.length" class="org-detail__empty mb-0">Todos están en el árbol.</p>
                  <div v-for="person in unplaced" :key="person.personId" class="mb-3">
                    <org-chart-person-card
                      :person="person"
                      :area-label="areas[person.area] || ''"
                      :highlighted="person.personId === highlightPersonId"
                      unplaced
                      @click="openPanel"
                    />
                    <v-btn
                      v-if="can('client-org-charts:update')"
                      x-small
                      text
                      class="mt-1 px-1"
                      @click="openPlace(person)"
                    >
                      <v-icon x-small left>mdi-sitemap</v-icon>
                      Ubicar en el árbol
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item>
          <v-card>
            <v-data-table
              :headers="candidateHeaders"
              :items="candidates"
              :loading="loadingCandidates"
              :items-per-page="25"
              no-data-text="No hay contactos por revisar"
              loading-text="Cargando contactos..."
            >
              <template #[`item.fullName`]="{ item }">
                {{ item.fullName }}
                <v-btn
                  v-if="item.linkedinUrl"
                  icon
                  x-small
                  :href="item.linkedinUrl"
                  target="_blank"
                  rel="noopener"
                  aria-label="Ver LinkedIn"
                >
                  <v-icon x-small>mdi-linkedin</v-icon>
                </v-btn>
              </template>
              <template #[`item.actions`]="{ item }">
                <div v-if="can('client-org-charts:update')" class="d-flex justify-end">
                  <v-btn small depressed color="primary" class="mr-1" @click="openConfirm(item)">Confirmar</v-btn>
                  <v-btn small text color="error" @click="askDiscard(item)">Descartar</v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <!-- Se monta al abrir la pestaña por primera vez, así las notas se piden una sola vez. -->
          <org-chart-notes v-if="notesOpened" :key="orgId" :pipedrive-org-id="orgId" />
        </v-tab-item>
      </v-tabs-items>

      <org-chart-person-panel
        v-model="panelOpen"
        :org-id="orgId"
        :person="selectedPerson"
        :placed="selectedIsPlaced"
        :manager-name="selectedManagerName"
        :areas="areas"
        @place="openPlace"
        @updated="onPersonUpdated"
        @removed="onPersonRemoved"
      />

      <place-person-modal
        v-model="placeOpen"
        :org-id="orgId"
        :person="placePerson"
        :nodes="nodes"
        :unplaced="unplaced"
        :areas="areas"
        @saved="onPlaced"
      />

      <add-person-modal
        v-model="addOpen"
        :org-id="orgId"
        :nodes="nodes"
        :unplaced="unplaced"
        :areas="areas"
        @created="onPersonCreated"
        @use-existing="onUseExisting"
      />

      <confirm-candidate-modal
        v-model="confirmOpen"
        :candidate="confirmingCandidate"
        :pipedrive-org-id="orgId"
        :nodes="nodes"
        :unplaced="unplaced"
        :areas="areas"
        @confirmed="onCandidateConfirmed"
        @changed="loadCandidates"
      />

      <v-dialog v-model="discardOpen" max-width="400px">
        <v-card v-if="discardingCandidate">
          <v-card-title>¿Descartar contacto?</v-card-title>
          <v-card-text>{{ discardingCandidate.fullName }} sale de la lista y no se agrega a Pipedrive.</v-card-text>
          <v-card-actions class="pa-4 pt-0">
            <v-spacer />
            <v-btn text :disabled="discarding" @click="discardOpen = false">Cancelar</v-btn>
            <v-btn color="error" depressed :loading="discarding" @click="discard">Descartar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import ClientOrgChartsService from '../services/client-org-charts.service';
import AddPersonModal from '../components/AddPersonModal.vue';
import ConfirmCandidateModal from '../components/ConfirmCandidateModal.vue';
import OrgChartNotes from '../components/OrgChartNotes.vue';
import OrgChartPersonCard from '../components/OrgChartPersonCard.vue';
import OrgChartPersonPanel from '../components/OrgChartPersonPanel.vue';
import OrgChartTree from '../components/OrgChartTree.vue';
import PlacePersonModal from '../components/PlacePersonModal.vue';
import { utilsMixin } from '../mixins/utilsMixin';
import { can } from '../utils/permissions';
import { errorMessage, isPipedriveError } from '../utils/org-chart.utils';

const TREE_TAB = 0;
const NOTES_TAB = 2;

export default {
  name: 'ClientOrgChartDetail',
  components: {
    AddPersonModal,
    ConfirmCandidateModal,
    OrgChartNotes,
    OrgChartPersonCard,
    OrgChartPersonPanel,
    OrgChartTree,
    PlacePersonModal
  },
  mixins: [utilsMixin],
  data() {
    return {
      organization: null,
      nodes: [],
      unplaced: [],
      pendingCandidates: 0,
      areas: {},
      loadingOrg: false,
      loadError: '',
      tab: TREE_TAB,
      notesOpened: false,
      candidates: [],
      loadingCandidates: false,
      searchPersonId: null,
      highlightPersonId: null,
      panelOpen: false,
      selectedPersonId: null,
      placeOpen: false,
      placePerson: null,
      addOpen: false,
      confirmOpen: false,
      confirmingCandidate: null,
      discardOpen: false,
      discardingCandidate: null,
      discarding: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
      candidateHeaders: [
        { text: 'Nombre', value: 'fullName', sortable: false },
        { text: 'Cargo', value: 'title', sortable: false },
        { text: 'Encontrado por', value: 'foundBy', sortable: false },
        { text: 'Jefe propuesto', value: 'proposedManagerName', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
    };
  },
  computed: {
    orgId() {
      return this.$route.params.pipedriveOrgId;
    },
    searchItems() {
      return [...this.nodes, ...this.unplaced].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    selectedPerson() {
      return this.findPerson(this.selectedPersonId);
    },
    selectedIsPlaced() {
      return this.nodes.some(node => node.personId === this.selectedPersonId);
    },
    selectedManagerName() {
      const person = this.selectedPerson;
      if (!person || person.managerPersonId === null || person.managerPersonId === undefined) return '';
      const manager = this.findPerson(person.managerPersonId);
      return manager ? manager.name : '';
    }
  },
  watch: {
    orgId() {
      this.loadAll();
    },
    tab(value) {
      if (value === NOTES_TAB) this.notesOpened = true;
    },
    searchPersonId(personId) {
      this.highlightPersonId = personId === undefined ? null : personId;
    }
  },
  created() {
    this.loadAll();
  },
  methods: {
    can,
    loadAll() {
      this.organization = null;
      this.loadOrganization();
      this.loadCandidates();
    },
    findPerson(personId) {
      if (personId === null || personId === undefined) return null;
      return [...this.nodes, ...this.unplaced].find(person => person.personId === personId) || null;
    },
    async loadOrganization() {
      this.loadingOrg = true;
      const res = await ClientOrgChartsService.getOrganization(this.orgId);
      this.loadingOrg = false;
      if (res.status === 'ok') {
        this.loadError = '';
        this.organization = res.data.organization;
        this.nodes = res.data.nodes || [];
        this.unplaced = res.data.unplaced || [];
        this.pendingCandidates = res.data.pendingCandidates || 0;
        this.areas = (res.meta && res.meta.areas) || {};
        return;
      }
      if (isPipedriveError(res.code) || !this.organization) {
        this.loadError = errorMessage(res, 'No pudimos cargar el organigrama. Intenta de nuevo.');
        return;
      }
      this.showErrorSnackbar(errorMessage(res, 'No pudimos actualizar el organigrama.'));
    },
    async loadCandidates() {
      this.loadingCandidates = true;
      const res = await ClientOrgChartsService.getOrganizationCandidates(this.orgId);
      this.loadingCandidates = false;
      if (res.status === 'ok') {
        this.candidates = res.data || [];
        this.pendingCandidates = this.candidates.length;
      } else {
        this.showErrorSnackbar(errorMessage(res, 'No pudimos cargar los contactos por revisar.'));
      }
    },
    openPanel(person) {
      this.selectedPersonId = person.personId;
      this.panelOpen = true;
    },
    openPlace(person) {
      this.placePerson = person;
      this.placeOpen = true;
    },
    onPlaced({ personId, managerPersonId, area }) {
      const managerInTree = managerPersonId === null || this.nodes.some(node => node.personId === managerPersonId);
      if (!managerInTree) {
        // El jefe estaba sin ubicar: el backend decide cómo queda, se recarga.
        this.loadOrganization();
      } else {
        const unplacedIndex = this.unplaced.findIndex(person => person.personId === personId);
        if (unplacedIndex >= 0) {
          const [person] = this.unplaced.splice(unplacedIndex, 1);
          this.nodes.push({ ...person, managerPersonId, area });
        } else {
          this.nodes = this.nodes.map(node => (node.personId === personId ? { ...node, managerPersonId, area } : node));
        }
      }
      this.highlightPersonId = personId;
      this.showSuccessSnackbar('Listo, quedó en el árbol');
    },
    onPersonRemoved(person) {
      // Igual que el backend: quienes le reportaban pasan a su jefe.
      this.nodes = this.nodes
        .filter(node => node.personId !== person.personId)
        .map(node => (node.personId !== person.personId && node.managerPersonId === person.personId
          ? { ...node, managerPersonId: person.managerPersonId }
          : node));
      this.unplaced.push({ ...person, managerPersonId: null, area: null });
      this.panelOpen = false;
      this.showSuccessSnackbar('Listo, quedó en "Sin ubicar"');
    },
    onPersonUpdated({ personId, ...changes }) {
      const apply = person => (person.personId === personId ? { ...person, ...changes } : person);
      this.nodes = this.nodes.map(apply);
      this.unplaced = this.unplaced.map(apply);
      this.showSuccessSnackbar('Guardado en Pipedrive');
    },
    async onPersonCreated({ personId }) {
      this.showSuccessSnackbar('Guardado en Pipedrive');
      await this.loadOrganization();
      if (personId) this.highlightPersonId = personId;
    },
    async onUseExisting({ personId, placement }) {
      const person = this.findPerson(personId);
      if (!person) {
        this.showWarningSnackbar('Esa persona está en otra empresa en Pipedrive. Revísala allá.');
        return;
      }
      if (!placement) {
        this.highlightPersonId = personId;
        this.openPanel(person);
        return;
      }
      const res = await ClientOrgChartsService.savePosition(this.orgId, personId, placement);
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res, 'No pudimos ubicarla en el árbol.'));
        return;
      }
      this.onPlaced({ personId, ...placement });
    },
    openConfirm(candidate) {
      this.confirmingCandidate = candidate;
      this.confirmOpen = true;
    },
    async onCandidateConfirmed({ candidateId, personId }) {
      this.removeCandidate(candidateId);
      this.showSuccessSnackbar('Guardado en Pipedrive');
      await this.loadOrganization();
      if (personId) this.highlightPersonId = personId;
    },
    askDiscard(candidate) {
      this.discardingCandidate = candidate;
      this.discardOpen = true;
    },
    async discard() {
      this.discarding = true;
      const res = await ClientOrgChartsService.discardCandidate(this.discardingCandidate.id);
      this.discarding = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res, 'No pudimos descartarlo. Intenta de nuevo.'));
        return;
      }
      this.removeCandidate(this.discardingCandidate.id);
      this.discardOpen = false;
      this.showSuccessSnackbar('Contacto descartado');
    },
    removeCandidate(candidateId) {
      this.candidates = this.candidates.filter(candidate => candidate.id !== candidateId);
      this.pendingCandidates = this.candidates.length;
    }
  }
};
</script>

<style scoped>
.org-detail__title {
  font-size: 22px;
  font-weight: 600;
  color: var(--pw-text);
  line-height: 1.3;
}

.org-detail__meta {
  color: var(--pw-text-muted);
}

.org-detail__actions {
  gap: 8px;
}

/* min-width: 0 deja que el árbol scrollee dentro de su columna en vez de ensancharla. */
.org-detail__tree-col {
  min-width: 0;
}

.org-detail__search {
  max-width: 360px;
}

.org-detail__section {
  font-size: 16px;
}

.org-detail__empty {
  color: var(--pw-text-muted);
}
</style>
